import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ReactElement, useCallback } from 'react';
import { formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { BaseReportFormField } from 'shared/domain/report/types/model/common';
import { LabelledEntity } from 'shared/types/commonView';
import { LocaleCountryAndLanguageCode } from 'shared/types/locale';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { ReportTranslationKeys } from 'intl/keys';
import {
  iso6391ToFlagEmoji,
  localeOptionToIso6391,
  REPORT_LOCALE_OPTIONS,
} from 'shared/utils/locale/reportLanguageDisplay';

function LanguageFlag({ flag }: { flag: string }): ReactElement {
  return (
    <Box
      component='span'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 20,
        fontSize: 16,
        lineHeight: 1,
        flexShrink: 0,
        position: 'relative',
        top: '1px',
      }}
    >
      {flag}
    </Box>
  );
}

const LANGUAGE_SELECTOR_ID = 'report-wizard-language';

export function ReportWizardLanguageSelector(): ReactElement {
  const intl = useIntl();
  const { values, setValues } = useInputForm();

  const selectedOption = values.localeCode as LabelledEntity | undefined;
  const selectorLabel = formatTranslation(intl, ReportTranslationKeys.sendReportLanguage,);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const option = REPORT_LOCALE_OPTIONS.find(
        (item) => item._id === event.target.value
      );
      if (!option) {
        return;
      }
      setValues(BaseReportFormField.localeCode, option);
      LocalStorageManager.saveLastReportLocale(
        option._id as LocaleCountryAndLanguageCode
      );
    },
    [setValues]
  );

  const renderValue = useCallback(
    (selectedId: string) => {
      const option = REPORT_LOCALE_OPTIONS.find(
        (item) => item._id === selectedId
      );
      if (!option) {
        return null;
      }
      const iso = localeOptionToIso6391(option);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LanguageFlag flag={iso6391ToFlagEmoji(iso)} />
          <Typography
            component='span'
            variant='body2'
            sx={{ lineHeight: '20px' }}
            noWrap
          >
            {option.label}
          </Typography>
        </Box>
      );
    },
    []
  );

  return (
    <FormControl
      size='small'
      sx={{ width: 184, flexShrink: 0 }}
      data-qa='report_wizard_language_selector'
    >
      <InputLabel id={`${LANGUAGE_SELECTOR_ID}-label`}>
        {selectorLabel}
      </InputLabel>
      <Select
        labelId={`${LANGUAGE_SELECTOR_ID}-label`}
        id={`${LANGUAGE_SELECTOR_ID}-select`}
        label={selectorLabel}
        value={selectedOption?._id ?? ''}
        onChange={handleChange}
        renderValue={renderValue}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
      >
        {REPORT_LOCALE_OPTIONS.map((option) => {
          const iso = localeOptionToIso6391(option);
          return (
            <MenuItem
              key={option._id}
              value={option._id}
              data-qa={`report_wizard_language_option_${iso}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageFlag flag={iso6391ToFlagEmoji(iso)} />
                <Typography variant='body2' sx={{ lineHeight: '20px' }}>
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
